import type { Maybe } from '@/helpers/types';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { MaybeRefOrGetter } from '@vueuse/core';

import { toValue } from 'vue';
import { computed, onMounted } from 'vue';

/**
 * Injects third-party VTR tracking scripts for specific campaigns.
 * Scripts are only injected when the campaign name matches the provided campaign set.
 */
export function useVtrTracking({
	sponsoredAd,
	vtrCampaigns,
	vtrScript,
}: {
	sponsoredAd: MaybeRefOrGetter<Maybe<SponsoredAdFragment>>;
	/** Campaign names to track for VTR */
	vtrCampaigns: Set<string>;
	/** The VTR tracking script to inject */
	vtrScript: string;
}) {
	const isVTRTrackingCampaign = computed(() => {
		const currentCampaign = toValue(sponsoredAd)?.campaign?.name ?? null;
		return currentCampaign != null && vtrCampaigns.has(currentCampaign);
	});

	onMounted(() => {
		if (!isVTRTrackingCampaign.value) return;

		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.text = vtrScript;
		document.body.appendChild(script);
	});
}
